import React from 'react';
import GuidelineSection from '../components/GuidelineSection';
import SubSection from '../components/SubSection';
import Table from '../components/Table';
import TableOfContents from '../components/TableOfContents';
const OffGuidelineDrugs: React.FC = () => {
  const tocItems = [{
    id: 'introduction',
    title: 'Introduction',
    level: 1
  }, {
    id: 'drug-sensitivity',
    title: 'Drug Sensitivity Tests',
    level: 1
  }, {
    id: 'off-guideline-examples',
    title: 'Off-Guideline Drug Examples',
    level: 1
  }, {
    id: 'cancer-commons',
    title: 'Cancer Commons Study',
    level: 1
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-463">
      <div className="lg:col-span-3" data-id="element-464">
        <GuidelineSection id="off-guideline-drugs" title="3. OFF-GUIDELINE DRUGS" data-id="element-465">
          <SubSection id="introduction" title="3.1 Introduction" data-id="element-466">
            <p className="mb-4" data-id="element-467">
              The NCCN Guidelines include FDA-approved drugs selected by the
              NCCN Guidelines committee based on clinical trials and other
              evidence. Many FDA-approved drugs do not appear in the NCCN
              Guidelines for NSCLC. These drugs are considered 'off-guideline'
              for this cancer.
            </p>
            <p className="mb-4" data-id="element-468">
              There is a newer class of diagnostic tests, called Drug
              Sensitivity Tests (DSTs) or Functional Tests, that can test
              off-guideline drugs against your live cancer cells. If your cancer
              cells respond to a drug in a laboratory setting, there may be a
              good chance your cancer will respond to the drug administered to
              you.
            </p>
            <p className="mb-4 font-medium" data-id="element-469">
              Two main challenges to pursuing off-guideline drugs:
            </p>
            <ol className="list-decimal pl-6 mb-6 space-y-2" data-id="element-470">
              <li data-id="element-471">
                Your oncologist may be reluctant to administer an off-guideline
                drug
              </li>
              <li data-id="element-472">
                Your health insurance company may not pay for an off-guideline
                drug
              </li>
            </ol>
            <p className="mb-4" data-id="element-473">
              Cancer Commons has launched an Off-Guideline Drug Study that can
              provide access to one or more DSTs for free and can help you
              overcome these challenges.
            </p>
          </SubSection>
          <SubSection id="drug-sensitivity" title="3.2 Drug Sensitivity Tests (DSTs)" data-id="element-474">
            <p className="mb-4" data-id="element-475">Table: Available DST Platforms for NSCLC</p>
            <Table headers={['Test Name', 'Vendor', 'Sample Type', 'Turnaround', 'Key Features', 'Curator Evaluation']} rows={[['Travera RapidSelection™[56]', 'Travera', 'Fresh tumor tissue, blood, pleural fluid', '2 days', 'Ultra-fast mass-based measurement of cell response; tests up to 20 FDA-approved drugs per order; 80% predictive accuracy in early studies; 100+ drugs validated', 'Emerging evidence (Orange)'], ['Patient-Derived Organoids (PDO)[57]', 'Multiple vendors (Xilis, SEngine, Champions)', 'Fresh tumor tissue or pleural fluid', '14-21 days', 'Tests 50-100+ drugs including off-label options; 75-90% concordance with clinical outcomes', 'Emerging evidence (Orange)'], ['Patient-Derived Xenografts (PDX)', 'Champions Oncology, JSI', 'Fresh tumor tissue', '3-6 months', 'In vivo testing, most physiologically relevant', 'Strong but slow (Orange)'], ['High-throughput cell culture assay', 'Various research institutions', 'Fresh tumor cells', '7-14 days', 'Rapid screening of chemotherapy agents', 'Emerging evidence (Orange)'], ['CD-DST (Collagen Gel Droplet)', 'Primarily Japan-based', 'Fresh surgical specimens', '14 days', 'Validated in Japanese NSCLC populations', 'Moderate evidence (Orange)'], ['Circulating Tumor Cells (CTC) Testing', 'Epic Sciences, others', 'Blood draw', '10-14 days', 'Non-invasive, can track resistance', 'Investigational (Red)']]} data-id="element-476" />
            <p className="font-medium mt-6 mb-2" data-id="element-477">
              Requirements for All DST Platforms:
            </p>
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-478">
              <div className="flex items-center" data-id="element-479">
                <input type="checkbox" className="mr-2" data-id="element-480" />
                <span data-id="element-481">Fresh tumor tissue (most DSTs)</span>
              </div>
              <div className="flex items-center" data-id="element-482">
                <input type="checkbox" className="mr-2" data-id="element-483" />
                <span data-id="element-484">
                  OR malignant pleural effusion (for some PDO models and
                  Travera)
                </span>
              </div>
              <div className="flex items-center" data-id="element-485">
                <input type="checkbox" className="mr-2" data-id="element-486" />
                <span data-id="element-487">
                  OR blood sample (for CTC-based approaches and Travera)
                </span>
              </div>
              <div className="flex items-center" data-id="element-488">
                <input type="checkbox" className="mr-2" data-id="element-489" />
                <span data-id="element-490">Adequate viable tumor cells</span>
              </div>
              <div className="flex items-center" data-id="element-491">
                <input type="checkbox" className="mr-2" data-id="element-492" />
                <span data-id="element-493">Coordination with surgery/biopsy team</span>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg mt-6 mb-6" data-id="element-494">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-495">
                Important Note on Evidence Level:
              </h4>
              <p data-id="element-496">
                Patient-derived organoid testing and other functional assays
                maintain emerging/primarily research-based status as of October
                2025—not yet standard of care. While some CLIA-certified
                commercial platforms exist, DST testing is not widely integrated
                into routine clinical practice. The evidence level classifies as
                Level 2-3 (emerging technology with promising but limited
                prospective validation).[58] Multiple retrospective and
                proof-of-concept studies demonstrate 75-90% concordance between
                DST drug testing and clinical outcomes, but large-scale
                prospective clinical trials remain limited.
              </p>
            </div>
          </SubSection>
          <SubSection id="off-guideline-examples" title="3.3 Examples of Off-Guideline Drugs That May Show Sensitivity" data-id="element-497">
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-498">
              <li data-id="element-499">
                Topotecan: Approved for small cell lung cancer, may work in some
                NSCLC
              </li>
              <li data-id="element-500">
                Irinotecan: Approved for colorectal cancer, tested in NSCLC
              </li>
              <li data-id="element-501">5-Fluorouracil (5-FU): Widely used in GI cancers</li>
              <li data-id="element-502">Oxaliplatin: Alternative platinum agent</li>
              <li data-id="element-503">Mitomycin C: Older chemotherapy with renewed interest</li>
              <li data-id="element-504">
                Targeted agents approved for other cancers: May show activity if
                similar pathway alterations exist
              </li>
            </ul>
          </SubSection>
          <SubSection id="cancer-commons" title="3.4 Cancer Commons Off-Guideline Drug Study" data-id="element-505">
            <p className="mb-4" data-id="element-506">
              Cancer Commons offers a comprehensive program to help patients
              access and benefit from drug sensitivity testing:
            </p>
            <p className="font-medium mb-2" data-id="element-507">Program Benefits:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-508">
              <li data-id="element-509">Free access to one or more DST platforms</li>
              <li data-id="element-510">
                Navigator support to coordinate tissue collection and testing
              </li>
              <li data-id="element-511">Medical oncology consultation to interpret DST results</li>
              <li data-id="element-512">
                Insurance advocacy support for off-guideline drug coverage
              </li>
              <li data-id="element-513">
                Oncologist education materials to facilitate off-guideline
                prescribing
              </li>
            </ul>
            <p className="mb-4" data-id="element-514">
              <strong data-id="element-515">How to Enroll:</strong> Contact Cancer Commons directly
              through their website or speak with your Cancer Commons Navigator
              about eligibility for the Off-Guideline Drug Study.
            </p>
          </SubSection>
        </GuidelineSection>
      </div>
      <div className="lg:col-span-1" data-id="element-516">
        <div className="lg:sticky lg:top-20" data-id="element-517">
          <TableOfContents items={tocItems} data-id="element-518" />
        </div>
      </div>
    </div>;
};
export default OffGuidelineDrugs;